// VmCostGraph.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { MdShowChart } from "react-icons/md";
import { useVmCost } from "../../hooks/useVmCost";
import VmFilter from "../dashboardComponents/AlertsComponents/vmFilter";
import HistoryDateFilter from "../common/HistoryDateFilter";

const COLOR_PALETTE = [
  "#dc2626",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#ef4444",
  "#6366f1",
  "#14b8a6",
  "#d946ef",
  "#f97316",
  "#22c55e",
  "#eab308",
  "#a855f7",
];

const VmCostGraph = () => {
  const { getMonthlyCostsByDateRange } = useVmCost();
  const [selectedVmId, setSelectedVmId] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState([]);
  const [vmCostsList, setVmCostsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);
  const isFetching = useRef(false);

  const handleRangeChange = useCallback(
    (start, end) => {
      if (start !== startDate || end !== endDate) {
        setStartDate(start);
        setEndDate(end);
      }
    },
    [startDate, endDate],
  );

  const handleVmChange = useCallback(
    (vmId) => {
      if (vmId !== selectedVmId) {
        setSelectedVmId(vmId);
      }
    },
    [selectedVmId],
  );

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate || isFetching.current) return;

    isFetching.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await getMonthlyCostsByDateRange(
        selectedVmId,
        startDate,
        endDate,
      );

      if (isMounted.current && result.success && result.data) {
        const { isAllVms, months, vmCosts, costs, vmId, vmName } = result.data;

        if (isAllVms) {
          setVmCostsList(vmCosts);
          const transformedData = months.map((month, monthIndex) => {
            const dataPoint = { month };
            vmCosts.forEach((vm) => {
              dataPoint[vm.vmName] = vm.costs[monthIndex];
            });
            return dataPoint;
          });
          setChartData(transformedData);
        } else {
          const singleVm = [{ vmId, vmName, costs }];
          setVmCostsList(singleVm);
          const transformedData = months.map((month, monthIndex) => {
            const dataPoint = { month };
            dataPoint[vmName] = costs[monthIndex];
            return dataPoint;
          });
          setChartData(transformedData);
        }
      } else if (isMounted.current && !result.success) {
        setError(result.message || "Failed to load data");
        setChartData([]);
        setVmCostsList([]);
      }
    } catch (err) {
      console.error("Error fetching cost data:", err);
      if (isMounted.current) {
        setError("Failed to load cost data");
        setChartData([]);
        setVmCostsList([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      isFetching.current = false;
    }
  }, [selectedVmId, startDate, endDate, getMonthlyCostsByDateRange]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [selectedVmId, startDate, endDate, fetchData]);

  if (loading && chartData.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <MdShowChart className="text-primary-cyan text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">VM Cost Trends</h3>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <HistoryDateFilter onRangeChange={handleRangeChange} />
            <VmFilter selectedVmId={selectedVmId} onVmChange={handleVmChange} />
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 ml-3">Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (error && chartData.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <MdShowChart className="text-primary-cyan text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">VM Cost Trends</h3>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <VmFilter selectedVmId={selectedVmId} onVmChange={handleVmChange} />
            <HistoryDateFilter onRangeChange={handleRangeChange} />
          </div>
        </div>
        <div className="text-center py-12">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (chartData.length === 0 && !loading) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <MdShowChart className="text-primary-cyan text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">VM Cost Trends</h3>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <VmFilter selectedVmId={selectedVmId} onVmChange={handleVmChange} />
            <HistoryDateFilter onRangeChange={handleRangeChange} />
          </div>
        </div>
        <div className="text-center py-12">
          <p className="text-gray-400">No VM cost data available for selected period</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-2 font-semibold">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <MdShowChart className="text-primary-cyan text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">VM Cost Trends</h3>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <HistoryDateFilter onRangeChange={handleRangeChange} />
          <VmFilter selectedVmId={selectedVmId} onVmChange={handleVmChange} />
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="min-w-[400px] sm:min-w-0" style={{ width: "100%", height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                tickLine={{ stroke: "#374151" }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                tickLine={{ stroke: "#374151" }}
                tickFormatter={(value) => `$${value}`}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ color: "#9CA3AF", fontSize: "10px" }}
                formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>}
              />
              {vmCostsList.map((vm, idx) => (
                <Line
                  key={vm.vmId}
                  type="monotone"
                  dataKey={vm.vmName}
                  stroke={COLOR_PALETTE[idx % COLOR_PALETTE.length]}
                  strokeWidth={2}
                  dot={{
                    fill: COLOR_PALETTE[idx % COLOR_PALETTE.length],
                    stroke: COLOR_PALETTE[idx % COLOR_PALETTE.length],
                    strokeWidth: 2,
                    r: 2,
                  }}
                  activeDot={{
                    fill: COLOR_PALETTE[idx % COLOR_PALETTE.length],
                    stroke: "#fff",
                    strokeWidth: 2,
                    r: 4,
                  }}
                  animationDuration={1000}
                  animationBegin={idx * 100}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VmCostGraph;